import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, Transition } from '@headlessui/react';
import { Bars3Icon, BellIcon, ChatBubbleOvalLeftIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { LoaderFunctionArgs, MetaDescriptor, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import {
  Outlet,
  useLoaderData,
  useLocation,
  useNavigation,
  useOutletContext,
  useRevalidator,
  useRouteLoaderData,
  useSearchParams,
} from '@remix-run/react';
import cn from 'classnames';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { useMetaViewContent } from '~/components/Pixels/useMetaViewContent';
import PoweredByBeehiiv from '~/components/PoweredByBeehiiv/PoweredByBeehiiv';
import EnablePushNotificationPrompt from '~/components/PWA/EnablePushNotificationPrompt';
import InstallPrompt from '~/components/PWA/InstallPrompt';
import { isCommunityInstallPath, PWA_PROMPT_STORAGE_KEYS } from '~/components/PWA/pwaPromptConfig';
import {
  COMMUNITY_ROUTES,
  communitySidebarWidthStyle,
  isCommunityFeedPath,
  isCommunitySpacePath,
  isDirectoryRoute,
  isMessagesRoute,
  SORT_ENABLED,
} from '~/constants/community';
import { env } from '~/env';
import { usePushNotification } from '~/hooks/usePushNotification';
import { useScrollMemory } from '~/hooks/useScrollMemory';
import type { CommunityPost } from '~/interfaces/community';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';
import getCommunity from '~/services/api/community/getCommunity.server';
import getContentPages from '~/services/api/community/getContentPages.server';
import getCurrentMember from '~/services/api/community/getCurrentMember.server';
import getProfileFields from '~/services/api/community/getProfileFields.server';
import getSpaces from '~/services/api/community/getSpaces.server';
import { getCommunityNotificationUnreadCount } from '~/services/api/community/notifications.server';
import { loaderSession } from '~/session';
import { resolveCommunityDestination } from '~/utils/buildPostAuthRedirectUrl';
import getServerTranslation from '~/utils/getServerTranslation/index.server';
import redirectWithToast from '~/utils/redirectWithToast/index.server';
import toast from '~/utils/toast';

import type { OpenPostComposerOptions } from './community/_components/PostComposer/context/composerContext';

import { Avatar } from './community/_components/ui/Avatar';
import { GlassIconButton } from './community/_components/ui/GlassIconButton';
import { GlassIconLink } from './community/_components/ui/GlassIconLink';
import { IconButton } from './community/_components/ui/IconButton';
import { PillButton } from './community/_components/ui/PillButton';
import { ReadOnlyBadge } from './community/_components/ui/ReadOnlyBadge';

import { AnswerQuestionModal } from './community/_components/AnswerQuestionModal';
import { ChannelUpgradeModal } from './community/_components/ChannelUpgradeModal';
import { useChannelUpgradeHandoff } from './community/_components/ChannelUpgradeModal/useChannelUpgradeHandoff';
import { CommunityAuthForm } from './community/_components/CommunityAuthForm';
import { CityChannelMenu } from './community/_components/CommunityFeed/_components/CityChannelMenu';
import { FeedSkeleton } from './community/_components/CommunityFeed/_components/FeedSkeleton';
import { MobileSortSheet } from './community/_components/CommunityFeed/_components/MobileSortSheet';
import { CommunityIdentity } from './community/_components/CommunityIdentity';
import { CommunityInviteModal } from './community/_components/CommunityInviteModal';
import { CommunitySearchModal } from './community/_components/CommunitySearchModal';
import { CommunityUpgradeModal } from './community/_components/CommunityUpgradeModal';
import { useUpgradeJoinHandoff } from './community/_components/CommunityUpgradeModal/useUpgradeJoinHandoff';
import { ContentUpgradeModal } from './community/_components/ContentUpgradeModal';
import { useContentUpgradeHandoff } from './community/_components/ContentUpgradeModal/useContentUpgradeHandoff';
import { JoinRefusedModal } from './community/_components/JoinRefusedModal';
import { PostComposerProvider, PostComposerSchedulerModal } from './community/_components/PostComposer';
import { RequiredProfileSetupOverlay } from './community/_components/RequiredProfileSetupOverlay';
import { RestrictionBanner } from './community/_components/RestrictionBanner';
import { Sidebar } from './community/_components/Sidebar';
import { MobileDrawerHeader } from './community/_components/Sidebar/_components/MobileDrawerHeader';
import { SidebarSkeleton } from './community/_components/Sidebar/SidebarSkeleton';
import { TopBar } from './community/_components/TopBar';
import { MobileTopBarActions } from './community/_components/TopBar/MobileTopBarActions';
import { TopBarShell } from './community/_components/TopBar/TopBarShell';
import { CommunityActionsProvider } from './community/_context/CommunityActionsProvider';
import { CommunityDataProvider } from './community/_context/CommunityDataProvider';
import { useChannelUpgradeGate } from './community/_hooks/useChannelUpgradeGate';
import { useCommunityAccess } from './community/_hooks/useCommunityAccess';
import { useCommunityAppObservations } from './community/_hooks/useCommunityAppObservations';
import { useCommunityDisplayName } from './community/_hooks/useCommunityDisplayName';
import { useCommunityJoinGate } from './community/_hooks/useCommunityJoinGate';
import { useCommunityLogoUrl } from './community/_hooks/useCommunityLogoUrl';
import { useCommunityMembership } from './community/_hooks/useCommunityMembership';
import { useContentUpgradeGate } from './community/_hooks/useContentUpgradeGate';
import { useMobilePageInfo } from './community/_hooks/useMobilePageInfo';
import { useProfilePinFetcher } from './community/_hooks/useProfilePinFetcher';
import { useRefetchFeedsOnAccessChange } from './community/_hooks/useRefetchFeedsOnAccessChange';
import { useSpace } from './community/_hooks/useSpace';
import { useSpaceAccess } from './community/_hooks/useSpaceAccess';
import { SPACE_MEMBERSHIP_ACTION } from './community/_hooks/useSpaceMembershipFetcher';
import { useSpaceMembershipToast } from './community/_hooks/useSpaceMembershipToast';
import { buildScrollMemoryKey } from './community/_utils/buildScrollMemoryKey';
import { communityIsAccessible } from './community/_utils/communityAccess';
import { useCommunityThemeStyle } from './community/_utils/communityTheme';
import { needsProfileOnboarding, needsRequiredProfileSetup } from './community/_utils/needsProfileOnboarding';
import {
  buildCommunityOnboardRedirectUrl,
  buildCommunityRootRedirectUrl,
  isCommunityGuestRestrictedRoute,
  redirectGuestToLogin,
} from './community/_utils/redirectGuestToLogin.server';
import resolveCommunityInviteView from './community/_utils/resolveCommunityInviteView.server';
import {
  isAnswerQuestionReload,
  shouldRevalidateCommunityRoute,
} from './community/_utils/shouldRevalidateCommunityRoute';
import { validateLumaEmbedUrl } from './community/events/_components/validateLumaEmbedUrl';

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const pathname = url.pathname;

  const site = await context.getSite(request, session);
  const destination = resolveCommunityDestination(url.searchParams);

  if (!site.publication.community_enabled || !site.publication.has_community) {
    throw new Response('Not found', { status: 404 });
  }

  const [spacesResponse, community, currentMember, invite, contentPages] = await Promise.all([
    getSpaces(request, session),
    getCommunity(request, session).catch(() => null),
    getCurrentMember(request, session).catch(() => null),
    resolveCommunityInviteView(request, session, url),
    getContentPages(request, session),
  ]);

  // A ban cuts all community access, not just posting/commenting. Gate here in the
  // loader (not the component) so community content never reaches the browser.
  // /community/banned is a top-level route (not nested under this layout), so this
  // loader never runs there and there's no redirect loop. Keep the query string, because
  // right after sign-in the jwt_token is only in the URL.
  if (currentMember?.banned) {
    throw redirect(`${COMMUNITY_ROUTES.banned}${url.search}`);
  }

  // A member who follows an invite link has nothing to accept. Drop the invite params
  // and tell them they're already in, rather than surfacing the invite modal.
  if (currentMember && invite) {
    const { t } = await getServerTranslation(request);
    throw redirect(
      redirectWithToast({
        baseUrl: COMMUNITY_ROUTES.root,
        toast: 'success',
        message: t('accept_community_invite.already_accepted.body'),
      }),
    );
  }

  // A member has nothing to join. A sign-in that returns to the join intent (the upgrade
  // modal's "Already a member?" link) would otherwise show them the terms form again.
  if (currentMember && url.searchParams.get('modal') === 'community_join') {
    const target = destination ? new URL(destination, url.origin) : null;
    if (target && target.searchParams.get('modal') !== 'community_join') {
      const jwtToken = url.searchParams.get('jwt_token');
      if (jwtToken) target.searchParams.set('jwt_token', jwtToken);
      throw redirect(`${target.pathname}${target.search}`);
    }

    url.searchParams.delete('modal');
    url.searchParams.delete('upgrade_return');
    throw redirect(`${pathname}${url.search}`);
  }

  // Restricted routes (messages, notifications) require an actual community member.
  // currentMember is null for guests, expired/invalid tokens, and logged-in non-members.
  if (!currentMember && isCommunityGuestRestrictedRoute(pathname)) {
    throw redirectGuestToLogin(request);
  }

  if (!currentMember && pathname.replace(/\/$/, '') !== COMMUNITY_ROUTES.root) {
    throw redirect(buildCommunityRootRedirectUrl(request));
  }

  const spaces = spacesResponse?.spaces;
  const joinedSpaces = spacesResponse?.joined_spaces ?? [];
  const spaceAccess = spacesResponse?.space_access ?? {};
  const unreadSpaceIds = spacesResponse?.unread_space_ids ?? [];

  let notificationUnreadCount: number | null = null;
  if (currentMember) {
    notificationUnreadCount = await getCommunityNotificationUnreadCount(request, session);
  }

  const shouldOnboard = needsProfileOnboarding(currentMember);
  if (shouldOnboard && pathname !== COMMUNITY_ROUTES.onboard) {
    throw redirect(buildCommunityOnboardRedirectUrl(request));
  }

  if (!shouldOnboard && pathname === COMMUNITY_ROUTES.onboard) {
    throw redirect(`${COMMUNITY_ROUTES.root}${url.search}`);
  }

  const isMessagesEnabled = Boolean(community?.dms_enabled && currentMember?.dms_enabled);
  const eventsEmbedUrl = validateLumaEmbedUrl(community?.luma_embed_url ?? null);
  const accessible = communityIsAccessible(community);
  const asksProfileQuestion = Object.values(spaceAccess).some((access) => access.profile_field_id);
  let profileFields: Awaited<ReturnType<typeof getProfileFields>>['profile_fields'] = [];
  if (needsRequiredProfileSetup({ member: currentMember, accessible })) {
    profileFields = (await getProfileFields(request, session)).profile_fields;
  } else if (asksProfileQuestion) {
    // The question only adds a button, so a failure leaves the channel locked rather than breaking the community.
    profileFields = await getProfileFields(request, session).then(
      ({ profile_fields }) => profile_fields,
      () => [],
    );
  }

  return json({
    spaces,
    joinedSpaces,
    spaceAccess,
    unreadSpaceIds,
    contentPages,
    community,
    currentMember,
    notificationUnreadCount,
    isMessagesEnabled,
    eventsEmbedUrl,
    invite,
    destination,
    communityMediaHost: env.COMMUNITY_MEDIA_HOST,
    profileFields,
  });
};

const COMMUNITY_LOGO_LINK_RELS = ['icon', 'apple-touch-icon', 'apple-touch-startup-image'];

const isLogoLink = (descriptor: MetaDescriptor): boolean =>
  'tagName' in descriptor && descriptor.tagName === 'link' && 'rel' in descriptor
    ? COMMUNITY_LOGO_LINK_RELS.includes(String(descriptor.rel))
    : false;

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const inherited = matches.flatMap((match) => match.meta ?? []);
  const { name, logo_url: logoUrl } = data?.community ?? {};

  return inherited.map((descriptor) => {
    if (name && 'title' in descriptor && !('tagName' in descriptor)) return { title: name };
    return logoUrl && isLogoLink(descriptor) ? { ...descriptor, href: logoUrl } : descriptor;
  });
};

// Defer form-action opt-outs (e.g. follow/unfollow) to shouldRevalidateCommunityRoute
// for a single source of truth across the community route tree, then add the layout's
// pathname-change trigger: the layout data (spaces, site) should re-fetch on any path
// change even when Remix's default wouldn't.
export const shouldRevalidate: ShouldRevalidateFunction = (args) => {
  // Layout explicitly opts in on space membership so the channels refetch brings
  // fresh joined_spaces. Child routes still skip via the shared util.
  if (args.formAction === SPACE_MEMBERSHIP_ACTION) return true;
  // The answer question modal reads the reloaded access to say whether the answer opened the channel.
  if (isAnswerQuestionReload(args)) return true;
  // Leaving onboarding: refetch so the sidebar shows the just-saved profile.
  if (args.currentUrl.pathname === COMMUNITY_ROUTES.onboard && args.nextUrl.pathname !== COMMUNITY_ROUTES.onboard) {
    return true;
  }
  // Entering a channel reads the member's current access. Leaving one drops the unread cue the visit cleared.
  if (
    (isCommunitySpacePath(args.currentUrl.pathname) || isCommunitySpacePath(args.nextUrl.pathname)) &&
    args.currentUrl.pathname !== args.nextUrl.pathname
  ) {
    return true;
  }
  if (!shouldRevalidateCommunityRoute(args)) return false;
  return args.defaultShouldRevalidate || args.currentUrl.pathname !== args.nextUrl.pathname;
};

const DRAWER_TITLE_ID = 'community-drawer-title';

const CommunityLayout = () => {
  const { t } = useTranslation();
  // Forward the root App's outlet context so child routes (and components
  // like PostPageProvider) can access cookies, isLoggedIn, admin, etc.
  const outletContext = useOutletContext<OutletContext>();
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [composerDefaultSpaceSlug, setComposerDefaultSpaceSlug] = useState<string | undefined>();
  const [composerQuotedPost, setComposerQuotedPost] = useState<CommunityPost | undefined>();
  const [composerEditPost, setComposerEditPost] = useState<CommunityPost | undefined>();
  const [composerOpenId, setComposerOpenId] = useState(0);
  const {
    spaces,
    spaceAccess,
    community,
    currentMember,
    notificationUnreadCount,
    isMessagesEnabled,
    invite,
    profileFields,
  } = useLoaderData<typeof loader>();
  useMetaViewContent(community?.id, community ? { content_type: 'community' } : null);
  useCommunityAppObservations(
    Boolean(community?.app_adoption_tracking_enabled),
    community?.publication_id,
    currentMember?.id,
  );
  useRefetchFeedsOnAccessChange(spaceAccess);
  const [searchParams] = useSearchParams();
  const { currentModal, handleCloseModal, handleOpenModal } = useGlobalModals();
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const publication = rootData?.site?.publication;
  const isLoggedIn = Boolean(rootData?.isLoggedIn);
  const isFetchingSubscription = rootData?.isFetchingSubscription ?? false;
  const isMember = Boolean(currentMember);
  const navigationV2Enabled = Boolean(community?.navigation_v2_enabled);
  const { isCommunityAccessible } = useCommunityAccess();
  const showRequiredProfileSetup = needsRequiredProfileSetup({
    member: currentMember,
    accessible: isCommunityAccessible,
  });
  // Join buttons exist outside the feed banner, so a closed or refused community still answers the join intent with a
  // modal.
  const joinGate = useCommunityJoinGate();
  // Channel upgrade is its own intent, routed by `space_id` rather than by joinGate.
  const channelUpgradeGate = useChannelUpgradeGate();
  const channelUpgradeHandoff = useChannelUpgradeHandoff();
  const contentUpgradeGate = useContentUpgradeGate();
  const contentUpgradeHandoff = useContentUpgradeHandoff(contentUpgradeGate.isGranted);
  const isSignInModalOpen = currentModal === 'community_login';
  const { pathname } = useLocation();
  const destination = resolveCommunityDestination(searchParams);
  const joinModalRedirectTo = destination ?? pathname;
  const upgradeJoinHandoff = useUpgradeJoinHandoff(destination);
  const pageInfo = useMobilePageInfo(spaces ?? []);
  const themeStyle = useCommunityThemeStyle(community);
  const { permissions, openJoinModal, openSignInModal } = useCommunityMembership();
  const { canPostAnywhere } = useSpaceAccess();
  const space = useSpace();

  const lockFeedScroll = !isMember;

  // Save/restore <main> scrollTop across in-layout navigation. The globally
  // mounted ScrollRestoration (~/components/ScrollRestoration) only sees
  // window.scrollY, which is always 0 here (the layout's <main> is the actual
  // scroll container), so we use a focused element-scoped hook instead.
  const mainRef = useScrollMemory<HTMLElement>({ keyFor: buildScrollMemoryKey });
  const navigation = useNavigation();
  const pendingPathname = navigation.location?.pathname;
  const isNavigatingToFeed =
    navigation.state === 'loading' &&
    pendingPathname != null &&
    pendingPathname !== pathname &&
    isCommunityFeedPath(pendingPathname);
  const isEventsRoute = pathname.replace(/\/$/, '') === COMMUNITY_ROUTES.events;
  const isOnboardingRoute = pathname === COMMUNITY_ROUTES.onboard;
  const { revalidate } = useRevalidator();

  const communityPwaEnabled = Boolean(community?.pwa_enabled);
  const communityDisplayName = useCommunityDisplayName();
  const communityLogoUrl = useCommunityLogoUrl();
  const showCommunityPwaPrompts = communityPwaEnabled && isLoggedIn && isMember;
  const communityPwaBranding = useMemo(
    () =>
      community && communityLogoUrl
        ? {
            name: community.name,
            logoUrl: communityLogoUrl,
            subtitle: publication?.name,
          }
        : undefined,
    [community, communityLogoUrl, publication?.name],
  );

  usePushNotification({
    icon: communityPwaEnabled ? communityLogoUrl : undefined,
  });

  // Stable toast id so layout revalidations don't stack duplicates while the
  // spaces API stays down.
  useEffect(() => {
    if (spaces == null) {
      toast.error(t('community.sidebar.spaces_load_error'), { id: 'community-spaces-load-error' });
    }
  }, [spaces, t]);

  useEffect(() => {
    if (!isMember) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isMember, revalidate]);

  const closeMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen(false);
  }, []);

  // show toast after space join/leave; refresh of joinedSpaces happens via
  // the layout's shouldRevalidate opt-in on SPACE_MEMBERSHIP_ACTION
  useSpaceMembershipToast();

  useProfilePinFetcher();

  const handleNavClick = () => {
    closeMobileDrawer();
  };

  const openComposer = useCallback(
    (options?: OpenPostComposerOptions) => {
      const { defaultSpaceSlug, quotedPost, editPost } = options ?? {};
      if (!isMember) {
        openJoinModal();
        return;
      }

      if (!permissions.canCreatePost) return;

      setComposerDefaultSpaceSlug(defaultSpaceSlug);
      setComposerEditPost(editPost);
      setComposerQuotedPost(editPost ? undefined : quotedPost);
      setComposerOpenId((id) => id + 1);
      setIsComposerOpen(true);
    },
    [openJoinModal, isMember, permissions.canCreatePost],
  );

  const communityHeader = (
    <>
      <Avatar
        src={communityLogoUrl}
        alt={communityDisplayName}
        className="size-8 shrink-0"
        fallbackClassName="bg-gray-300"
      />
      <span className="text-[15px] font-semibold leading-5 tracking-[-0.15px] truncate">{communityDisplayName}</span>
    </>
  );

  return (
    <CommunityDataProvider isMember={isMember}>
      <PostComposerProvider openComposer={openComposer}>
        <CommunityActionsProvider>
          <div className={cn('fixed inset-0 flex bg-community-bg', { 'flex-col': navigationV2Enabled })}>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: injects theme CSS vars deterministically before paint */}
            <style
              dangerouslySetInnerHTML={{ __html: `${themeStyle}\n${communitySidebarWidthStyle(navigationV2Enabled)}` }}
            />
            <TopBarShell
              enabled={navigationV2Enabled}
              topBar={
                !isOnboardingRoute && (
                  <TopBar
                    notificationUnreadCount={notificationUnreadCount}
                    isFetchingSubscription={isFetchingSubscription}
                  />
                )
              }
            >
              {/* Desktop sidebar — hidden on onboarding so users can't navigate away */}
              {!isOnboardingRoute && (
                <div className="hidden h-full shrink-0 overflow-y-auto md:block">
                  {isFetchingSubscription ? (
                    <SidebarSkeleton />
                  ) : (
                    <Sidebar spaces={spaces} notificationUnreadCount={notificationUnreadCount} />
                  )}
                </div>
              )}
              {/* Mobile drawer */}
              <Transition show={isMobileDrawerOpen && !isOnboardingRoute} as={Fragment}>
                <Dialog
                  as="div"
                  className="relative z-50 md:hidden"
                  onClose={closeMobileDrawer}
                  aria-labelledby={DRAWER_TITLE_ID}
                >
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-100"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="fixed inset-0 bg-black/60" />
                  </Transition.Child>

                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-100"
                    enterFrom="-translate-x-full"
                    enterTo="translate-x-0"
                    leave="ease-in duration-100"
                    leaveFrom="translate-x-0"
                    leaveTo="-translate-x-full"
                  >
                    <Dialog.Panel className="fixed inset-0 flex flex-col bg-community-bg text-community-text-on-bg">
                      <Dialog.Title id={DRAWER_TITLE_ID} className="sr-only">
                        Community navigation
                      </Dialog.Title>
                      {/* Mobile nav bar: avatar + name left, close right */}
                      {navigationV2Enabled ? (
                        <MobileDrawerHeader onClose={closeMobileDrawer} />
                      ) : (
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3 min-w-0">{communityHeader}</div>
                          <IconButton aria-label="Close navigation" onClick={closeMobileDrawer}>
                            <XMarkIcon className="size-4" />
                          </IconButton>
                        </div>
                      )}

                      {/* Sidebar content fills remaining space */}
                      <div className="flex-1 overflow-y-auto">
                        {isFetchingSubscription ? (
                          <SidebarSkeleton variant="mobile" />
                        ) : (
                          <Sidebar
                            spaces={spaces}
                            onNavClick={handleNavClick}
                            variant="mobile"
                            notificationUnreadCount={notificationUnreadCount}
                          />
                        )}
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </Dialog>
              </Transition>
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden md:p-3">
                {/* Mobile nav bar */}
                {pageInfo.showTopbar && (
                  <div className="md:hidden flex items-center justify-between pl-2 pr-4 py-3 text-community-text-on-bg">
                    <div className="flex items-center gap-1 min-w-0">
                      {!isOnboardingRoute && (
                        <IconButton
                          aria-label="Open navigation"
                          aria-expanded={isMobileDrawerOpen}
                          className="size-8 shrink-0"
                          onClick={() => setIsMobileDrawerOpen(true)}
                        >
                          <Bars3Icon className="size-4" />
                        </IconButton>
                      )}
                      {navigationV2Enabled ? (
                        <CommunityIdentity />
                      ) : (
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar
                            src={communityLogoUrl}
                            alt={communityDisplayName}
                            className="size-10"
                            fallbackClassName="bg-gray-300"
                          />
                          <span className="text-[15px] font-semibold leading-5 tracking-[-0.15px] truncate">
                            {communityDisplayName}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {navigationV2Enabled && !isOnboardingRoute && (
                        <MobileTopBarActions notificationUnreadCount={notificationUnreadCount} />
                      )}
                      {!navigationV2Enabled && isLoggedIn && !isOnboardingRoute && (
                        <>
                          {isMessagesEnabled && (
                            <GlassIconLink to={COMMUNITY_ROUTES.messages} aria-label="Messages">
                              <ChatBubbleOvalLeftIcon className="size-4" />
                            </GlassIconLink>
                          )}
                          <span className="relative inline-flex">
                            <GlassIconLink to={COMMUNITY_ROUTES.notifications} aria-label="Notifications">
                              <BellIcon className="size-4" />
                            </GlassIconLink>
                            {typeof notificationUnreadCount === 'number' && notificationUnreadCount > 0 ? (
                              <span
                                className="pointer-events-none absolute -right-0.5 -top-0.5 size-2 rounded-full bg-community-accent ring-2 ring-community-bg"
                                aria-hidden
                              />
                            ) : null}
                          </span>
                        </>
                      )}
                      {!isLoggedIn && (
                        <PillButton
                          variant="primary"
                          onClick={() => openSignInModal()}
                          className="inline-flex shrink-0 px-4 transition-opacity hover:opacity-90"
                          aria-label={t('shared.cta.sign_in')}
                        >
                          {t('shared.cta.sign_in')}
                        </PillButton>
                      )}
                    </div>
                  </div>
                )}

                <main
                  ref={mainRef}
                  className={cn('flex-1 bg-white md:rounded-3xl shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]', {
                    'overflow-hidden': lockFeedScroll,
                    'overflow-y-auto': !lockFeedScroll,
                    'rounded-t-[32px]': pageInfo.showTopbar,
                    'flex min-h-0 flex-col': isMessagesRoute(pathname) || isEventsRoute || isDirectoryRoute(pathname),
                  })}
                >
                  {/* Mobile page nav — hidden on post detail pages */}
                  {pageInfo.showPageHeader && (
                    <div className="md:hidden flex items-center justify-between px-4 py-2 h-16 sticky top-0 bg-white z-10 rounded-t-[32px]">
                      <div className="w-20" />
                      {pageInfo.showChannelDropdown && SORT_ENABLED ? (
                        <MobileSortSheet label={pageInfo.title} />
                      ) : (
                        <h1 className="min-w-0 truncate text-xl font-semibold leading-6 tracking-[-0.4px]">
                          {pageInfo.title}
                        </h1>
                      )}
                      <div className="flex items-center justify-end w-20">
                        {pageInfo.readOnly && <ReadOnlyBadge />}
                        {space?.city && <CityChannelMenu space={space} />}
                        {pageInfo.plusAction === 'create' && canPostAnywhere && (
                          <GlassIconButton
                            aria-label={t('community.composer.create_post')}
                            onClick={() => openComposer()}
                          >
                            <PlusIcon className="size-4" />
                          </GlassIconButton>
                        )}
                        {/* Channel join action removed for MVP: channel joining is hidden from the UI. */}
                      </div>
                    </div>
                  )}
                  {permissions.isRestricted && <RestrictionBanner />}
                  {isNavigatingToFeed ? <FeedSkeleton /> : <Outlet context={outletContext} />}
                  <PostComposerSchedulerModal
                    key={composerOpenId}
                    isOpen={isComposerOpen}
                    onClose={() => setIsComposerOpen(false)}
                    spaces={spaces ?? []}
                    defaultSpaceSlug={composerDefaultSpaceSlug}
                    quotedPost={composerQuotedPost}
                    editPost={composerEditPost}
                  />
                  {!isMessagesRoute(pathname) && !isEventsRoute && !isDirectoryRoute(pathname) && (
                    <PoweredByBeehiiv utmMedium="community_footer" variant="pill" />
                  )}
                </main>
              </div>
            </TopBarShell>
            <CommunitySearchModal />
            {invite && <CommunityInviteModal invite={invite} publicationName={publication?.name ?? 'Community'} />}
            {currentModal === 'community_join' &&
              (joinGate === 'upgrade' || joinGate === 'closed') &&
              !isFetchingSubscription && <CommunityUpgradeModal handoff={upgradeJoinHandoff} />}
            {currentModal === 'community_join' && joinGate === 'refused' && (
              <JoinRefusedModal
                onClose={() => handleCloseModal({ preventScrollReset: true })}
                publicationName={publication?.name ?? 'Community'}
              />
            )}
            {channelUpgradeGate.isOpen && channelUpgradeGate.space && (
              <ChannelUpgradeModal space={channelUpgradeGate.space} handoff={channelUpgradeHandoff} />
            )}
            {contentUpgradeGate.target && (
              <ContentUpgradeModal {...contentUpgradeGate.target} handoff={contentUpgradeHandoff} />
            )}
            {isMember && currentModal === 'community_answer_question' && (
              <AnswerQuestionModal spaceId={searchParams.get('space_id')} />
            )}
            {showRequiredProfileSetup ? (
              <RequiredProfileSetupOverlay
                fields={profileFields}
                firstName={currentMember?.subscription.first_name ?? ''}
                avatarUrl={currentMember?.subscription.avatar_url ?? undefined}
                hasAvatar={currentMember?.subscription.has_avatar}
                headline={currentMember?.headline ?? ''}
                location={currentMember?.location ?? ''}
                locationSuggestion={currentMember?.location_suggestion}
              />
            ) : null}
            <CommunityAuthForm
              mode="join"
              isOpen={currentModal === 'community_join' && joinGate === 'joinable'}
              onClose={() => handleCloseModal({ preventScrollReset: true })}
              onSwitchMode={() => handleOpenModal('community_login', { preventScrollReset: true })}
              isLoggedIn={isLoggedIn}
              publicationName={publication?.name ?? 'Community'}
              community={community}
              publicationId={publication?.id}
              redirectTo={joinModalRedirectTo}
              destination={destination}
            />
            <CommunityAuthForm
              mode="login"
              isOpen={isSignInModalOpen}
              onClose={() => handleCloseModal({ preventScrollReset: true })}
              onSwitchMode={() => handleOpenModal('community_join', { preventScrollReset: true })}
              isLoggedIn={isLoggedIn}
              publicationName={publication?.name ?? 'Community'}
              community={community}
              publicationId={publication?.id}
              redirectTo={joinModalRedirectTo}
            />
            {showCommunityPwaPrompts && communityPwaBranding ? (
              <>
                <InstallPrompt
                  canShowOnPath={isCommunityInstallPath}
                  storageKey={PWA_PROMPT_STORAGE_KEYS.communityInstall}
                  branding={communityPwaBranding}
                  variant="community"
                />
                <EnablePushNotificationPrompt
                  storageKey={PWA_PROMPT_STORAGE_KEYS.communityPush}
                  branding={communityPwaBranding}
                  variant="community"
                />
              </>
            ) : null}
          </div>
        </CommunityActionsProvider>
      </PostComposerProvider>
    </CommunityDataProvider>
  );
};

export default CommunityLayout;
